import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LogoWhite from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import { LocaleSwitcher } from '../../i18n/components/LocaleSwitcher';
import { COMMON_KEYS, NS } from '../../i18n/constants/keys';
import { BellIcon } from '../../icons/animated';
import { getImageSrc } from '../../utils/helpers/image-utils';
import { formatDatePretty } from '../../utils/helpers/time';
import { RootState } from '../../utils/redux_store/store';
import { Notifications, Profile, SettingsMenu } from './Functions';
import { MobileMenu } from './MobileMenu';
import { AppsDockTrigger, AppsFolder } from './AppsFolder';
import styles from './NavBar.module.css';

export interface NavBarProps {
  variant?: 'default' | 'job_posting';
}

export const NavBar: React.FC<NavBarProps> = ({ variant = 'default' }) => {
  const isJobPosting = variant === 'job_posting';
  const { t } = useTranslation(NS.common);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Universal Shortcut to toggle Apps Launchpad (Ctrl+K, Cmd+K, Ctrl+Space, Alt+A)
  useEffect(() => {
    if (isJobPosting) return;
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (
        (isCmdOrCtrl && (e.key === 'k' || e.key === 'K')) ||
        (isCmdOrCtrl && (e.key === ' ' || e.code === 'Space')) ||
        (e.altKey && (e.key === 'a' || e.key === 'A'))
      ) {
        e.preventDefault();
        setIsAppsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isJobPosting]);

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const today = new Date();

  const formattedDate = formatDatePretty(today);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <>
      <nav
          className={`h-[var(--nav-height)] z-50 flex justify-between items-center fixed top-0 px-3 sm:px-4 lg:px-5 py-1.5 w-full ${styles.navbar}`}
          style={{ zIndex: 1000, backgroundColor: 'var(--navbar-bg, var(--brand-secondary, #093725))' }}
      >
        { /* Apps Dock Trigger & Logo Section */ }
        <div className={`flex items-center gap-3 sm:gap-3.5 ${styles.logoContainer}`}>
          {!isJobPosting && (
            <AppsDockTrigger
              onClick={() => setIsAppsOpen(true)}
              isOpen={isAppsOpen}
            />
          )}
          <Link to={isJobPosting ? '/jobs' : '/'} className='flex items-center gap-2 min-w-0'>
            <img
                src={getImageSrc(companyLogo, LogoWhite)}
                className={` max-h-12 ${styles.logo}`}
                alt={t(COMMON_KEYS.navbar.companyLogoAlt)}
            />
          </Link>
        </div>

        { /* Desktop Right Section */ }
        <div className={`hidden lg:flex items-center gap-1.5 ${styles.rightSection}`}>
          {!isJobPosting && (
            <div className='flex flex-col shrink-0 mr-3'>
              <h1
                className='text-md font-medium tracking-tight'
                style={{ color: 'var(--navbar-text, #ffffff)' }}
              >
                {t(COMMON_KEYS.navbar.welcome, {
                  name: localStorage.getItem('firstname') || '',
                })}
              </h1>
              <p
                className='text-xs font-medium tracking-tight'
                style={{ color: 'var(--navbar-text-muted, rgba(255, 255, 255, 0.8))' }}
              >
                { formattedDate }
              </p>
            </div>
          )}

          {!isJobPosting && (
            <div className={`${styles.actionButton} relative flex items-center justify-center rounded-lg p-1`}>
              <Notifications />
            </div>
          )}

          <div className={`${styles.actionButton} relative flex items-center justify-center rounded-lg p-1`}>
            <LocaleSwitcher variant='navbar' />
          </div>

          {!isJobPosting && (
            <div className={`${styles.profileButton} rounded-full`}>
              <Profile />
            </div>
          )}
        </div>

        { /* Mobile Right Section */ }
        <div
            className={`flex lg:hidden items-center gap-2 shrink-0 ${styles.rightSection}`}
        >
          <LocaleSwitcher variant='navbar' />
          {!isJobPosting && (
            <>
              { /* Mobile Notifications - Show icon only */ }
              <div className={`${styles.actionButton} rounded-lg`}>
                <button className='relative flex h-9 w-9 items-center justify-center rounded-lg' aria-label="Notifications">
                  <BellIcon size={20} className='text-[var(--navbar-text,#ffffff)]' />
                  { /* Optional notification badge */ }
                  <span
                      className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${styles.notificationBadge}`}
                  ></span>
                </button>
              </div>

              { /* Mobile System Settings - non-candidates only */ }
              { localStorage.getItem('role') !== 'Candidate' && (
                <div className={`${styles.actionButton} flex items-center justify-center rounded-lg p-1`}>
                  <SettingsMenu />
                </div>
              ) }

              { /* Mobile Menu Button */ }
              <button
                  onClick={toggleMobileMenu}
                  className={`${styles.mobileMenuButton} h-9 w-9 rounded-lg flex items-center justify-center`}
                  aria-label='Toggle mobile menu'
              >
                <div className='flex flex-col w-5 h-5 justify-center items-center'>
                  <span
                      className={`block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm ${
                        isMobileMenuOpen
                          ? 'rotate-45 translate-y-1'
                          : '-translate-y-0.5'
                      }`}
                      style={{ backgroundColor: 'var(--navbar-text, #ffffff)' }}
                  ></span>
                  <span
                      className={`block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm my-0.5 ${
                        isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                      }`}
                      style={{ backgroundColor: 'var(--navbar-text, #ffffff)' }}
                  ></span>
                  <span
                      className={`block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm ${
                        isMobileMenuOpen
                          ? '-rotate-45 -translate-y-1'
                          : 'translate-y-0.5'
                      }`}
                      style={{ backgroundColor: 'var(--navbar-text, #ffffff)' }}
                  ></span>
                </div>
              </button>
            </>
          )}
        </div>
      </nav>

      { /* Mobile Menu */ }
      {!isJobPosting && (
        <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
        />
      )}

      { /* iOS App Folder Launchpad */ }
      {!isJobPosting && (
        <AppsFolder
          isOpen={isAppsOpen}
          onClose={() => setIsAppsOpen(false)}
        />
      )}
    </>
  );
};
